'use strict';

const libPictProvider = require('pict-provider');
const libPlatformDocs = require('../help/PlatformDocs.js');
const libMarkdownEditor = require('pict-section-markdowneditor');

const RAIL_MOUNT = '#Doc-Rail';
const DETAIL_MOUNT = '#Doc-Detail';
const EDITOR_HOST_ID = 'Doc-Editor-Host';

/**
 * Documentation manager — the #/Documentation screen. Browses every platform help topic (the static registry
 * merged with topics discovered from PlatformDocumentIndex), shows each topic's status (platform default vs. this
 * customer's own copy vs. a customer-authored topic), and edits / creates / reverts them through the SAME
 * AcademyContent write path the F1 panel editor uses. Two modes, by session (reusing the app's gate): a customer-1
 * admin manages the canonical docs; a customer admin manages their own overrides + topics; everyone else reads.
 *
 * State lives in AppData.Documentation; the view renders a shell with #Doc-Rail + #Doc-Detail mounts and this
 * provider assigns hand-built HTML into them (SamplePlan pattern), mounting the markdown editor into the detail.
 */
class DocumentationProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
	}

	get app() { return this.pict.PictApplication; }
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get state()
	{
		if (!this.pict.AppData.Documentation)
		{
			this.pict.AppData.Documentation =
			{
				Loaded: false, Loading: false, Areas: [],
				SelectedHash: '', SelectedTitle: '', SelectedStatus: '', SelectedIsRegistry: false,
				OwnRowID: 0, Markdown: '', Editing: false, Adding: '', ConfirmDelete: false, Error: '',
			};
		}
		return this.pict.AppData.Documentation;
	}

	/** The edit mode from the app's shared gate: 'canonical' | 'override' | null (read-only). */
	editMode() { return (this.app && (typeof this.app._platformDocsEditMode === 'function')) ? this.app._platformDocsEditMode() : null; }

	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID) : 0; }

	// ── Data ──────────────────────────────────────────────────────────────────

	/** Load every area's topic list (registry ∪ discovered) with per-topic status, then render. */
	loadAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return; }
		tmpState.Loading = true;
		this.render();
		const tmpAreaNames = libPlatformDocs.AREAS.map((pArea) => pArea.Area);
		const tmpCaller = this.callerCustomer();
		Promise.all(tmpAreaNames.map((pArea) => this._fetchIndex(pArea))).then((pResults) =>
		{
			tmpState.Areas = tmpAreaNames.map((pArea, pIndex) =>
			{
				const tmpDiscovered = pResults[pIndex] || [];
				const tmpByHash = {};
				tmpDiscovered.forEach((pDoc) => { if (pDoc && pDoc.Hash) { tmpByHash[pDoc.Hash] = pDoc; } });
				const tmpKnown = {};
				const tmpTopics = libPlatformDocs.docsInArea(pArea).map((pDoc) =>
				{
					tmpKnown[pDoc.Hash] = true;
					const tmpDisc = tmpByHash[pDoc.Hash];
					return this._topicEntry(pDoc.Hash, pDoc.Nav, (tmpDisc ? tmpDisc.IDCustomer : 1), true, tmpCaller);
				});
				tmpDiscovered
					.filter((pDoc) => pDoc && pDoc.Hash && !tmpKnown[pDoc.Hash])
					.forEach((pDoc) => tmpTopics.push(this._topicEntry(pDoc.Hash, pDoc.Title || pDoc.Hash, pDoc.IDCustomer, false, tmpCaller)));
				return { Area: pArea, Title: this._areaTitle(pArea), Topics: tmpTopics };
			});
			tmpState.Loaded = true;
			tmpState.Loading = false;
			this.render();
		});
	}

	/** A rail topic entry with a computed status. */
	_topicEntry(pHash, pLabel, pIDCustomer, pIsRegistry, pCaller)
	{
		let tmpStatus = 'default';
		if (pCaller === 1) { tmpStatus = 'canonical'; }
		else if (Number(pIDCustomer) === pCaller) { tmpStatus = pIsRegistry ? 'customized' : 'own'; }
		return { Hash: pHash, Label: pLabel, IsRegistry: pIsRegistry, Status: tmpStatus };
	}

	_fetchIndex(pArea)
	{
		const tmpRestClient = this.restClient;
		if (!tmpRestClient || !pArea) { return Promise.resolve([]); }
		return new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`PlatformDocumentIndex/${encodeURIComponent(pArea)}`, (pError, pBody) => resolve((!pError && Array.isArray(pBody)) ? pBody : []));
		});
	}

	/**
	 * The caller's OWN AcademyContent row for a hash (id + title + content), or null when they have not
	 * authored an override.
	 *
	 * Resolved from PlatformDocument, which reports the IDCustomer and row id of whichever copy it served,
	 * rather than from a Meadow read by Hash. That read fetches the canonical customer-1 row the caller does
	 * not own, and MyCustomer — a post-fetch per-record assertion — then refuses the whole request, so it
	 * resolved null for every tenant looking at a topic that has a platform default. That reads as "no
	 * override" whether or not one exists, which is what saveAsync and the revert action key off: a tenant
	 * with an override would have POSTed a duplicate instead of updating it, and revert would have gone dark.
	 */
	_fetchOwnRow(pHash)
	{
		const tmpRestClient = this.restClient;
		if (!tmpRestClient) { return Promise.resolve(null); }
		const tmpCallerCustomer = this.callerCustomer();
		return new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`PlatformDocument/${encodeURIComponent(pHash)}`, (pError, pBody) =>
			{
				if (pError || !pBody || Array.isArray(pBody) || !pBody.Hash) { return resolve(null); }
				// Only the caller's own copy counts as an "own row" — the canonical one is not theirs to edit
				// in place. An API that does not report IDCustomer cannot be trusted either way, so treat it
				// as no override and let the caller author one.
				const tmpServedCustomer = Number(pBody.IDCustomer || 0);
				if (!tmpServedCustomer || !tmpCallerCustomer || (tmpServedCustomer !== tmpCallerCustomer)) { return resolve(null); }
				return resolve({ IDAcademyContent: pBody.IDAcademyContent, Title: pBody.Title, Content: pBody.Content });
			});
		});
	}

	/** The resolved doc (own-or-canonical) for a hash — used to seed a first customer override. */
	_fetchResolved(pHash)
	{
		const tmpRestClient = this.restClient;
		if (!tmpRestClient) { return Promise.resolve(null); }
		return new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`PlatformDocument/${encodeURIComponent(pHash)}`, (pError, pBody) => resolve((!pError && pBody && pBody.Content) ? pBody : null));
		});
	}

	// ── Interactions (reached from onclick as _Pict.providers.Documentation.X) ──

	/** Select a topic → load its own-row (id + content) or the canonical content, show it (read mode). */
	selectTopic(pHash)
	{
		const tmpState = this.state;
		tmpState.SelectedHash = pHash;
		tmpState.Editing = false;
		tmpState.Adding = '';
		tmpState.ConfirmDelete = false;
		tmpState.Error = '';
		const tmpRegistryDoc = libPlatformDocs.docByHash(pHash);
		tmpState.SelectedTitle = (tmpRegistryDoc && tmpRegistryDoc.Title) || pHash;
		tmpState.SelectedIsRegistry = !!tmpRegistryDoc;
		this.render();
		this._fetchOwnRow(pHash).then((pOwnRow) =>
		{
			if (tmpState.SelectedHash !== pHash) { return; }
			tmpState.OwnRowID = (pOwnRow && pOwnRow.IDAcademyContent) || 0;
			if (pOwnRow && (pOwnRow.Title)) { tmpState.SelectedTitle = pOwnRow.Title; }
			if (pOwnRow && (pOwnRow.Content != null)) { tmpState.Markdown = pOwnRow.Content; this.render(); return; }
			// No own row (a customer viewing a not-yet-overridden canonical topic): seed from the resolved doc.
			this._fetchResolved(pHash).then((pResolved) =>
			{
				if (tmpState.SelectedHash !== pHash) { return; }
				tmpState.Markdown = (pResolved && pResolved.Content) || '';
				this.render();
			});
		});
	}

	/** Enter edit mode for the selected topic. */
	startEdit()
	{
		if (!this.editMode() || !this.state.SelectedHash) { return; }
		this.state.Editing = true;
		this.state.Error = '';
		this.render();
	}

	/** Leave edit mode without saving. */
	cancelEdit() { this.state.Editing = false; this.state.ConfirmDelete = false; this.render(); }

	/** Save the editor's markdown: PUT an existing row, or POST a new override (server stamps the customer). */
	saveAsync()
	{
		const tmpState = this.state;
		const tmpRestClient = this.restClient;
		if (!tmpState.SelectedHash || !tmpRestClient) { return; }
		const tmpMarkdown = this._captureEditor();
		const tmpRecord = { Hash: tmpState.SelectedHash, Title: tmpState.SelectedTitle, Content: tmpMarkdown };
		const fDone = (pError) =>
		{
			if (pError)
			{
				tmpState.Error = 'Could not save — the server rejected the write (you may not have permission at this scope).';
				this.render();
				return;
			}
			tmpState.Markdown = tmpMarkdown;
			tmpState.Editing = false;
			this._invalidateCache();
			// Reload the row (its id + status may have changed — a first customer save just created the row).
			this.selectTopic(tmpState.SelectedHash);
			this.loadAsync();
		};
		if (tmpState.OwnRowID) { tmpRestClient.putJSON('AcademyContent', Object.assign({ IDAcademyContent: tmpState.OwnRowID }, tmpRecord), fDone); }
		else { tmpRestClient.postJSON('AcademyContent', tmpRecord, fDone); }
	}

	/** Whether the selected topic exposes a delete/revert action for this session. */
	canDeleteSelected()
	{
		const tmpState = this.state;
		if (!tmpState.OwnRowID) { return false; }
		const tmpMode = this.editMode();
		// Customer: delete their own row (override or their topic). Headlight: only a NON-registry canonical topic
		// (deleting a registry topic would orphan its static route/nav — that stays a code change).
		if (tmpMode === 'override') { return true; }
		if (tmpMode === 'canonical') { return !tmpState.SelectedIsRegistry; }
		return false;
	}

	armDelete() { if (this.canDeleteSelected()) { this.state.ConfirmDelete = true; this.render(); } }
	cancelDelete() { this.state.ConfirmDelete = false; this.render(); }

	/** Delete the selected topic's own row (revert to default, or remove a customer/new topic). */
	confirmDeleteAsync()
	{
		const tmpState = this.state;
		const tmpRestClient = this.restClient;
		if (!tmpState.OwnRowID || !tmpRestClient || (typeof tmpRestClient.deleteEntity !== 'function')) { return; }
		tmpRestClient.deleteEntity('AcademyContent', tmpState.OwnRowID, (pError) =>
		{
			if (pError)
			{
				tmpState.ConfirmDelete = false;
				tmpState.Error = 'Could not delete — the server rejected the request.';
				this.render();
				return;
			}
			tmpState.ConfirmDelete = false;
			tmpState.Editing = false;
			this._invalidateCache();
			// If it was a registry topic, it reverts to canonical (still selectable); otherwise it's gone.
			if (tmpState.SelectedIsRegistry) { this.selectTopic(tmpState.SelectedHash); }
			else { tmpState.SelectedHash = ''; this.render(); }
			this.loadAsync();
		});
	}

	// ── New topic ───────────────────────────────────────────────────────────────

	addTopic(pArea) { if (this.editMode() && pArea) { this.state.Adding = pArea; this.state.Error = ''; this.render(); this._focusNewTitle(); } }
	cancelAdd() { this.state.Adding = ''; this.render(); }

	createTopicAsync()
	{
		const tmpState = this.state;
		const tmpRestClient = this.restClient;
		if (!tmpState.Adding || !tmpRestClient) { return; }
		let tmpTitle = '';
		try { const tmpInput = document.getElementById('Doc-NewTopic-Title'); tmpTitle = tmpInput ? String(tmpInput.value || '').trim() : ''; }
		catch (pError) { tmpTitle = ''; }
		const tmpSlug = tmpTitle.replace(/[^A-Za-z0-9]+/g, ' ').trim().split(/\s+/).filter((pWord) => pWord.length > 0)
			.map((pWord) => pWord.charAt(0).toUpperCase() + pWord.slice(1)).join('');
		if (!tmpTitle || !tmpSlug) { tmpState.Error = 'Give the topic a title with at least one letter or number.'; this.render(); return; }
		const tmpExisting = {};
		libPlatformDocs.docsInArea(tmpState.Adding).forEach((pDoc) => { tmpExisting[pDoc.Hash] = true; });
		const tmpAreaState = tmpState.Areas.find((pArea) => pArea.Area === tmpState.Adding);
		if (tmpAreaState) { tmpAreaState.Topics.forEach((pTopic) => { tmpExisting[pTopic.Hash] = true; }); }
		let tmpFinalSlug = tmpSlug;
		let tmpCounter = 2;
		while (tmpExisting[`Doc-${tmpState.Adding}-${tmpFinalSlug}`]) { tmpFinalSlug = tmpSlug + tmpCounter; tmpCounter++; }
		const tmpHash = `Doc-${tmpState.Adding}-${tmpFinalSlug}`;
		tmpRestClient.postJSON('AcademyContent', { Hash: tmpHash, Title: tmpTitle, Content: `# ${tmpTitle}\n\n` }, (pError) =>
		{
			if (pError) { tmpState.Error = 'Could not create the topic — the server rejected the write (you may not have permission).'; this.render(); return; }
			tmpState.Adding = '';
			this._invalidateCache();
			this.selectTopic(tmpHash);
			this.state.Editing = true;
			this.loadAsync();
		});
	}

	// ── Render ─────────────────────────────────────────────────────────────────

	/** Assign the rail + detail HTML into their mounts, and (re)mount the editor when editing. */
	render()
	{
		if (this.pict.ContentAssignment.getElement(RAIL_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(RAIL_MOUNT, this._railHTML());
		this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
		if (this.state.Editing) { this._mountEditor(); }
		if (this.state.Adding) { this._focusNewTitle(); }
	}

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	_areaTitle(pArea) { return String(pArea || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2'); }

	_statusChip(pStatus)
	{
		const tmpMap =
		{
			canonical: [ 'Platform', 'doc-chip-canonical' ],
			default: [ 'Platform default', 'doc-chip-default' ],
			customized: [ 'Customized', 'doc-chip-custom' ],
			own: [ 'Your topic', 'doc-chip-custom' ],
		};
		const tmpEntry = tmpMap[pStatus] || tmpMap.default;
		return `<span class="doc-chip ${tmpEntry[1]}">${this._esc(tmpEntry[0])}</span>`;
	}

	_railHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Loaded) { return '<div class="doc-rail-loading">Loading topics…</div>'; }
		const tmpEditable = !!this.editMode();
		return tmpState.Areas.map((pArea) =>
		{
			const tmpItems = pArea.Topics.map((pTopic) =>
			{
				const tmpActive = (pTopic.Hash === tmpState.SelectedHash) ? ' is-active' : '';
				return `<div class="doc-rail-item${tmpActive}" onclick="_Pict.providers.Documentation.selectTopic('${this._esc(pTopic.Hash)}')">`
					+ `<span class="doc-rail-item-label">${this._esc(pTopic.Label)}</span>${this._statusChip(pTopic.Status)}</div>`;
			}).join('');
			const tmpAdd = tmpEditable
				? `<div class="doc-rail-add" onclick="_Pict.providers.Documentation.addTopic('${this._esc(pArea.Area)}')"><i class="fas fa-plus"></i> Add topic</div>`
				: '';
			return `<div class="doc-rail-group"><div class="doc-rail-head">${this._esc(pArea.Title)}</div>${tmpItems}${tmpAdd}</div>`;
		}).join('');
	}

	/** Which routes surface a topic in F1 (for the "appears on" hint). */
	_routesForHash(pHash)
	{
		return libPlatformDocs.ROUTE_DOC.filter((pEntry) => pEntry.Hash === pHash).map((pEntry) => pEntry.Route);
	}

	_detailHTML()
	{
		const tmpState = this.state;
		if (tmpState.Adding) { return this._newTopicHTML(); }
		if (!tmpState.SelectedHash) { return '<div class="doc-empty">Pick a topic on the left to view or edit it.</div>'; }
		const tmpError = tmpState.Error ? `<p class="doc-error">${this._esc(tmpState.Error)}</p>` : '';
		const tmpRoutes = this._routesForHash(tmpState.SelectedHash);
		const tmpWhere = tmpRoutes.length
			? `Opens with F1 on ${tmpRoutes.map((pRoute) => `<code>#${this._esc(pRoute)}</code>`).join(', ')}`
			: `A sibling topic in the ${this._esc(this._areaTitle(libPlatformDocs.areaFromHash(tmpState.SelectedHash)))} help`;
		const tmpHead = `<div class="doc-detail-head"><div><div class="doc-detail-title">${this._esc(tmpState.SelectedTitle)}</div>`
			+ `<div class="doc-detail-sub">${this._statusChip(this._selectedStatus())}<span class="doc-detail-where">${tmpWhere}</span></div></div>`
			+ this._detailActionsHTML() + '</div>';
		if (tmpState.Editing)
		{
			return tmpHead + tmpError + `<div id="${EDITOR_HOST_ID}" class="doc-editor-host"></div>` + this._deleteAreaHTML();
		}
		const tmpBody = this._parseMarkdown(tmpState.Markdown || '_Empty topic._');
		return tmpHead + tmpError + `<div class="doc-detail-body">${tmpBody}</div>`;
	}

	/** The read/edit action buttons in the detail header. */
	_detailActionsHTML()
	{
		const tmpState = this.state;
		const tmpMode = this.editMode();
		if (!tmpMode) { return ''; }
		if (tmpState.Editing)
		{
			return '<div class="doc-detail-actions">'
				+ '<button class="doc-btn doc-btn-primary" onclick="_Pict.providers.Documentation.saveAsync()">Save</button>'
				+ '<button class="doc-btn doc-btn-ghost" onclick="_Pict.providers.Documentation.cancelEdit()">Cancel</button></div>';
		}
		return `<div class="doc-detail-actions"><button class="doc-btn doc-btn-ghost" onclick="_Pict.providers.Documentation.startEdit()"><i class="fas fa-pen"></i> Edit</button></div>`;
	}

	/** The revert/delete row shown under the editor (customer overrides + non-registry topics). */
	_deleteAreaHTML()
	{
		const tmpState = this.state;
		if (!this.canDeleteSelected()) { return ''; }
		const tmpKnown = tmpState.SelectedIsRegistry;
		if (tmpState.ConfirmDelete)
		{
			const tmpQ = tmpKnown ? 'Delete this customer’s copy and revert to the platform default?' : 'Delete this topic?';
			return `<div class="doc-detail-actions doc-delrow"><span>${this._esc(tmpQ)}</span>`
				+ `<button class="doc-btn doc-btn-danger" onclick="_Pict.providers.Documentation.confirmDeleteAsync()">${tmpKnown ? 'Revert' : 'Delete'}</button>`
				+ '<button class="doc-btn doc-btn-ghost" onclick="_Pict.providers.Documentation.cancelDelete()">Keep it</button></div>';
		}
		return `<div class="doc-detail-actions doc-delrow"><button class="doc-btn doc-btn-danger" onclick="_Pict.providers.Documentation.armDelete()">${tmpKnown ? 'Revert to platform default' : 'Delete this topic'}</button></div>`;
	}

	_newTopicHTML()
	{
		const tmpState = this.state;
		const tmpNote = (this.editMode() === 'canonical')
			? 'New platform-default topic — visible to every customer once you save it.'
			: 'New topic for your customer only.';
		const tmpError = tmpState.Error ? `<p class="doc-error">${this._esc(tmpState.Error)}</p>` : '';
		return `<div class="doc-detail-head"><div class="doc-detail-title">Add a topic to ${this._esc(this._areaTitle(tmpState.Adding))}</div></div>`
			+ `<p class="doc-detail-note">${this._esc(tmpNote)}</p>${tmpError}`
			+ '<div class="doc-detail-actions"><input id="Doc-NewTopic-Title" type="text" class="doc-input" placeholder="Topic title" />'
			+ '<button class="doc-btn doc-btn-primary" onclick="_Pict.providers.Documentation.createTopicAsync()">Create</button>'
			+ '<button class="doc-btn doc-btn-ghost" onclick="_Pict.providers.Documentation.cancelAdd()">Cancel</button></div>';
	}

	_parseMarkdown(pMarkdown)
	{
		return (this.app && (typeof this.app._parseHelpMarkdown === 'function')) ? this.app._parseHelpMarkdown(pMarkdown) : this._esc(pMarkdown);
	}

	// ── Editor mount ─────────────────────────────────────────────────────────

	_mountEditor()
	{
		if (this.pict.ContentAssignment.getElement(`#${EDITOR_HOST_ID}`).length === 0) { return; }
		if (!this.pict.AppData.DocumentationEditor) { this.pict.AppData.DocumentationEditor = {}; }
		this.pict.AppData.DocumentationEditor.Segments = [ { Content: this.state.Markdown || '' } ];
		if (!this.pict.views['Documentation-Editor'])
		{
			this.pict.addView('Documentation-Editor',
				{
					ViewIdentifier: 'Documentation-Editor',
					TargetElementAddress: `#${EDITOR_HOST_ID}`,
					ContentDataAddress: 'AppData.DocumentationEditor.Segments',
					AutoRender: false, EnableRichPreview: true, ReadOnly: false,
				}, libMarkdownEditor);
		}
		try { this.pict.views['Documentation-Editor'].render(); }
		catch (pError) { this.pict.log.warn('Documentation editor failed to render', { error: String(pError) }); }
	}

	_captureEditor()
	{
		const tmpView = this.pict.views['Documentation-Editor'];
		if (tmpView && (typeof tmpView.marshalFromView === 'function')) { try { tmpView.marshalFromView(); } catch (pError) { /* editor gone */ } }
		const tmpSegments = (this.pict.AppData.DocumentationEditor && this.pict.AppData.DocumentationEditor.Segments) || [];
		return tmpSegments.map((pSeg) => (pSeg && pSeg.Content) || '').join('\n\n');
	}

	_focusNewTitle() { try { const tmpInput = document.getElementById('Doc-NewTopic-Title'); if (tmpInput) { tmpInput.focus(); } } catch (pError) { /* no-op */ } }

	_invalidateCache()
	{
		// Keep the F1 panel in sync: drop its per-hash + per-area caches for the edited topic.
		if (this.app)
		{
			if (this.app._academyHelpCache && this.state.SelectedHash) { this.app._academyHelpCache[this.state.SelectedHash] = undefined; }
			if (this.app._platformAreaIndexCache) { this.app._platformAreaIndexCache = {}; }
		}
	}

	/** Status of the currently-selected topic, from the loaded rail entry. */
	_selectedStatus()
	{
		const tmpState = this.state;
		let tmpStatus = 'default';
		(tmpState.Areas || []).some((pArea) => pArea.Topics.some((pTopic) =>
		{
			if (pTopic.Hash === tmpState.SelectedHash) { tmpStatus = pTopic.Status; return true; }
			return false;
		}));
		return tmpStatus;
	}
}

module.exports = DocumentationProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'Documentation',
	AutoInitialize: true,
	AutoInitializeOrdinal: 40,
};
